import React, {useEffect, useRef, useState} from 'react';
import {useDeleteReportGeneration, useGetGeneratedReportData} from "api/api-hooks/reports-hooks";
import {addToDate, formatDateTimeOrNow, formatTime} from "../../../utils/time-utils";
import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Column, ColumnFilterApplyClickParams} from "primereact/column";
import {IPeriodLoadingConfig} from "../product-template-table/utils/interfaces";
import {TemplateLoadingType} from "../product-template-table/utils/enums";
import {getTemplateLoadingRange} from "../product-template-table/utils/timeUtils";
import {DashboardReportsApi} from "../../../api/DashboardReportsApi";
import {RadioButton} from "primereact/radiobutton";
import {Dashboard} from "../../pages/dashboard/components/interfaces";
import {Dialog} from "primereact/dialog";
import GenerateNewReport from "./GenerateNewReport";
import {Toast, ToastSeverityType} from "primereact/toast";
import {ConfirmDialog} from "primereact/confirmdialog";
import GeneratedReportHistoryList from "./GeneratedReportHistoryList";
import {Tooltip} from "react-tooltip";
import MarkForRegeneration from "./MarkForRegeneration";
import RemoveReportRange from "./RemoveReportRange";
import IComponentTimed = Dashboard.IComponentTimed;
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import {ReportStatus, ReportType} from "../../../api/model/enums";
import {GeneratedDashboardReportResponse} from "../../../api/model/dashboard/GeneratedDashboardReportResponse";

type GeneratedReportTableProps = {
    ids: Array<number>,
    type?: ReportType,
    loadingConfig?: IPeriodLoadingConfig
    itemsPerPage?: number
    sort?: "ASC" | "DESC"
    referenceTimePattern?: string
    status?: ReportStatus

    superUserComponent?: boolean
} & IComponentTimed

type FilterType = {
    dashboardId?: Array<number>;
    onlyType?: ReportType;
}

const statusMap: Record<ReportStatus, any> = {
    SUCCESS: <i data-tooltip-content={"Success"} style={{color: "green"}}
                className="pi pi-check status-body-tooltip"></i>,
    FAILED: <i data-tooltip-content={"Failed"} style={{color: "red"}} className="pi pi-times status-body-tooltip"></i>,
    FAILED_RETRYING: <i data-tooltip-content={"Failed. Retrying..."} style={{color: "orange"}}
                        className="pi pi-exclamation-triangle status-body-tooltip"></i>,
    GENERATING: <i data-tooltip-content={"Generating"} className="pi pi-spin pi-spinner status-body-tooltip"></i>,
    MARKED_FOR_REGENERATION: <i data-tooltip-content={"Marked For Regeneration"} style={{color: "red"}}
                                className="pi pi-spin pi-hourglass status-body-tooltip"></i>,
    QUEUED: <i data-tooltip-content={"Queued"} style={{color: "orange"}}
               className="pi pi-spin pi-hourglass status-body-tooltip"></i>
}

const typeMap: Record<ReportType, any> = {
    MANUAL: <i data-tooltip-content={"Manual"} className="pi pi-user type-body-tooltip"></i>,
    SCHEDULED: <i data-tooltip-content={"Scheduled"} className="pi pi-server type-body-tooltip"></i>
}

const GeneratedReportTable = (props: GeneratedReportTableProps) => {
    const [from, setFrom] = useState<Date | undefined>(undefined)
    const [to, setTo] = useState<Date | undefined>(undefined);
    const [dateChanged, setDateChanged] = useState(false)

    const [filterInfo, setFilerInfo] = useState<FilterType>({})

    const [generateDialog, setGenerateDialog] = useState(false);
    const [removeRangeDialog, setRemoveRangeDialog] = useState(false)
    const [dialogFunction, setDialogFunction] = useState<"REMOVE" | "CLEANUP">("REMOVE")

    const toast = useRef<Toast | null>(null);

    const rowsPerPageOptions = props.itemsPerPage ? [props.itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
            return a.indexOf(x) == i;
        }) : [10, 20, 30];

    const {data, isLoading, isError, refetch} = useGetGeneratedReportData({
            id: props.ids,
            onlyType: props.type,
            from: from?.toISOString(),
            to: to?.toISOString(),
            sort: props.sort,
            status: props.status
                || [
                    ReportStatus.GENERATING,
                    ReportStatus.FAILED,
                    ReportStatus.FAILED_RETRYING,
                    ReportStatus.SUCCESS,
                    ReportStatus.QUEUED,
                    ReportStatus.MARKED_FOR_REGENERATION
                ]
        },
        {
            enabled: !!from && !!to
        })

    const {mutate: deleteReportGeneration} = useDeleteReportGeneration({
        onSuccess: () => {
            showToast("success", "", "Report generation deleted")
        },
        onMutate: () => {
            showToast("success", "", "Deleting report generation")
        },
        onFailure: () => {
            showToast("error", "", "Failed to delete report generation")
        }
    });

    useEffect(() => {
        setDatesByConfig()
    }, [])

    const setDatesByConfig = () => {
        if (props.loadingConfig) {
            let {start, end} = getTemplateLoadingRange({
                loadingType: TemplateLoadingType.RANGE,
                loadingConfig: props.loadingConfig
            }, props.referenceTime);

            setFrom(start);
            setTo(end);
        } else {
            setFrom(addToDate(new Date(), -1, "DAYS"))
            setTo(new Date())
        }
        setDateChanged(false)
    }

    const filterDashBoardId = (e: ColumnFilterApplyClickParams) => {
        // @ts-ignore
        const keyPart = e.constraints.constraints[0].value;

        if (keyPart) {

            const arr: Array<GeneratedDashboardReportResponse> = data as Array<GeneratedDashboardReportResponse>

            const ar: Array<number> = arr.map(ob => {
                if (ob.dashboardTitle.includes(keyPart) && ob.dashboardId) {
                    return ob.dashboardId as number
                }
            }).filter(id => id !== undefined) as number[];

            let uniqueArr: Array<number> = [];
            for (let i = 0; i < ar.length; i++) {
                if (uniqueArr.indexOf(ar[i]) === -1) {
                    uniqueArr.push(ar[i]);
                }
            }

            setFilerInfo(e => ({...e, dashboardId: uniqueArr}))
        } else {
            setFilerInfo(e => ({...e, dashboardId: undefined}))
        }
    }

    const onZipDownloadClick = () => {
        let dashboardIds = props.ids;
        let onlyType = props.type;

        if (filterInfo) {
            if (filterInfo.dashboardId) {
                if (filterInfo.dashboardId.length === 0) {
                    console.warn("No ids was filtered")
                } else {
                    dashboardIds = filterInfo.dashboardId
                }
            }

            if (filterInfo.onlyType) {
                onlyType = filterInfo.onlyType
            }
        }

        if (dashboardIds.length > 0 && from) {
            window.open(DashboardReportsApi.getDownloadZipUrl(dashboardIds, from.toISOString(), to?.toISOString(), onlyType))
        }
    }

    const reportTypesCheckboxFiltering = (options: any) => {
        return <div>
            <div className="field-radiobutton">
                <RadioButton inputId="type"
                             value={ReportType.SCHEDULED}
                             name="types"
                             onChange={(e) => {
                                 setFilerInfo(val => ({...val, onlyType: e.value}))
                                 options.filterCallback(e.value)
                             }}
                             checked={options.value === ReportType.SCHEDULED}
                />
                <label htmlFor="type">Scheduled</label>
            </div>
            <div className="field-radiobutton" style={{marginBottom: 0}}>
                <RadioButton inputId="type2"
                             value={ReportType.MANUAL}
                             name="types"
                             onChange={(e) => {
                                 setFilerInfo(val => ({...val, onlyType: e.value}))
                                 options.filterCallback(e.value)
                             }}
                             checked={options.value === ReportType.MANUAL}
                />
                <label htmlFor="type2">Manual</label>
            </div>
        </div>
    }

    const reportHistory = (rowData: GeneratedDashboardReportResponse) => {
        const [historyDialog, setHistoryDialog] = useState(false)
        return <>
            <Dialog header="Report History"
                    visible={historyDialog}
                    style={{minWidth: '10vw'}}
                    onHide={() => setHistoryDialog(false)}>
                <GeneratedReportHistoryList data={rowData.fileIds.map(e => ({file: e}))}
                                            dashboardId={rowData.dashboardId}
                                            genId={rowData.id}
                                            showToast={showToast}
                                            closeDialog={() => setHistoryDialog(false)}
                />
            </Dialog>
            <div className={"flex align-items-center"}>
                {rowData.fileIds && rowData.fileIds.length > 1 &&
                    <Button icon={"pi pi-list"} className="p-button-rounded p-button-text p-button-sm"
                            onClick={() => {
                                setHistoryDialog(true)
                            }}
                    />
                }
            </div>
        </>
    }

    const downloadReportBody = (rowData: GeneratedDashboardReportResponse) => {
        return (
            <div className={"flex align-items-center"}>
                {rowData.fileIds && rowData.fileIds.length > 0 &&
                    <Button icon={"pi pi-download"} className="p-button-rounded p-button-text p-button-sm"
                            onClick={() => {
                                window.open(DashboardReportsApi.getDownloadUrl(rowData.id, rowData.fileIds[rowData.fileIds.length - 1]))
                            }}
                    />
                }
            </div>
        ) || null;
    }

    const deleteGenerationBody = (rowData: GeneratedDashboardReportResponse) => {
        const [removeDialog, setRemoveDialog] = useState(false)

        const accept = () => {
            deleteReportGeneration({genId: rowData.id})
        }

        return <>
            <ConfirmDialog visible={removeDialog}
                           onHide={() => setRemoveDialog(false)}
                           message="Do you want to remove this report?"
                           header="Delete Confirmation"
                           icon="pi pi-info-circle"
                           acceptClassName={'p-button-danger'}
                           accept={accept}/>
            <div className={"flex align-items-center"}>
                <Button icon={"pi pi-trash"} style={{color: "red"}}
                        className="p-button-rounded p-button-text p-button-sm"
                        onClick={() => {
                            setRemoveDialog(true)
                        }}
                />
            </div>
        </>
    }

    const typeBody = (rowData: GeneratedDashboardReportResponse) => {
        return <>
            <Tooltip anchorSelect=".type-body-tooltip" delayShow={200}/>
            {typeMap[rowData.type]}
        </>
    }

    const statusBody = (rowData: GeneratedDashboardReportResponse) => {
        return <>
            <Tooltip anchorSelect=".status-body-tooltip" delayShow={200}/>
            {statusMap[rowData.status]}
        </>
    }

    const showToast = (severity: ToastSeverityType, summary: string, detail: string, lifetime: number = 5000) => {
        toast.current!!.show({severity: severity, summary: summary, detail: detail, life: lifetime});
    };

    if (isError) {
        return <div>Component error...</div>
    }

    return <>
        <Toast ref={toast}/>
        <div className={"flex flex-column gap-2"}>
            <div className={"flex gap-2 align-items-end justify-content-between"}>
                <div className={"flex gap-2 align-items-end"}>
                    <div className={"flex flex-column gap-1"}>
                        <span style={{fontWeight: "bold"}}>From:</span>
                        <CalendarWrapper value={from as Date} onChange={(e) => {
                            setDateChanged(true);
                            setFrom(e)
                        }}/>
                    </div>
                    <div className={"flex flex-column gap-1"}>
                        <span style={{fontWeight: "bold"}}>To:</span>
                        <CalendarWrapper value={to as Date} onChange={(e) => {
                            setDateChanged(true);
                            setTo(e)
                        }}/>
                    </div>
                    {dateChanged &&
                        <div>
                            <Button icon={"pi pi-refresh"}
                                    onClick={() => setDatesByConfig()}
                            />
                        </div>
                    }
                </div>
                <div className={"flex gap-1"}>
                    <div>
                        <Button icon={"pi pi-refresh"}
                                className="p-button-sm"
                                onClick={() => {
                                    refetch()
                                        .then(() => showToast("success", "", "Data reloaded successfully", 3000))
                                }}
                                disabled={!props.ids || props.ids.length === 0}
                        />
                    </div>
                    <div>
                        <Button icon={"pi pi-cog"}
                                className="p-button-sm"
                                label={"Generate"}
                                onClick={() => setGenerateDialog(true)}
                                disabled={!props.ids || props.ids.length === 0}
                        />
                    </div>

                    {props.superUserComponent &&
                        <div>
                            <Button icon={"pi pi-history"}
                                    className="p-button-sm p-button-danger"
                                    label={"Cleanup"}
                                    onClick={() => {
                                        setDialogFunction("CLEANUP")
                                        setRemoveRangeDialog(true)
                                    }}
                                    disabled={!props.ids || props.ids.length === 0}
                            />
                        </div>
                    }

                    {props.superUserComponent &&
                        <div>
                            <Button icon={"pi pi-trash"}
                                    className="p-button-sm p-button-danger"
                                    label={"Reports"}
                                    onClick={() => {
                                        setDialogFunction("REMOVE")
                                        setRemoveRangeDialog(true)
                                    }}
                                    disabled={!props.ids || props.ids.length === 0}
                            />
                        </div>
                    }
                    <div>
                        <Button icon={"pi pi-folder"}
                                className="p-button-sm"
                                label={"Download"}
                                onClick={onZipDownloadClick}
                                disabled={filterInfo.dashboardId?.length === 0 || props.ids.length === 0}
                        />
                    </div>
                </div>
            </div>
            <DataTable className={"generated-report-table"} size="small" value={data}
                       paginator responsiveLayout="scroll"
                       paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                       currentPageReportTemplate="Showing {first} - {last} / {totalRecords}"
                       rows={props.itemsPerPage || 10} rowsPerPageOptions={rowsPerPageOptions}
                       loading={isLoading}
                       sortMode="multiple"
            >
                <Column field={"dashboardTitle"}
                        header={"Report"}
                        sortable
                        filter
                        filterMatchMode={"contains"}
                        filterField="dashboardTitle"
                        onFilterApplyClick={filterDashBoardId}
                        onFilterClear={() => setFilerInfo(e => ({...e, dashboardId: undefined}))}
                        showFilterMatchModes={false}
                        showFilterMenuOptions={false}
                        filterPlaceholder={"Search"}
                />
                {!props.type && <Column field={"type"}
                                        header={"Type"}
                                        style={{width: "0"}}
                                        body={typeBody}
                                        sortable
                                        filter
                                        filterMatchMode={"contains"}
                                        filterElement={reportTypesCheckboxFiltering}
                                        onFilterClear={() => {
                                            setFilerInfo(e => ({...e, onlyType: undefined}))
                                        }}
                                        filterField="type"
                                        showFilterMatchModes={false}
                                        showFilterMenuOptions={false}
                                        filterPlaceholder={"Search"}
                />}

                <Column field={"status"} header={"Status"} style={{width: "0"}} body={statusBody}/>

                <Column field={"referenceTime"}
                        header={"Reference Time"}
                        body={e => formatDateTimeOrNow(props.referenceTimePattern || "yyyy-MM-dd HH:mm", e.referenceTime)}
                />
                <Column field={"scheduleTime"}
                        header={"Created"}
                        body={e => formatTime(e.generationTimeEnd)}
                />
                <Column body={(rowData) => MarkForRegeneration({rowData: rowData, showToast: showToast})}
                        style={{width: "0"}}/>
                <Column body={reportHistory} style={{width: "0"}}/>
                <Column body={downloadReportBody} style={{width: "0"}}/>
                <Column body={deleteGenerationBody} style={{width: "0"}}/>
            </DataTable>

        </div>


        <Dialog header="Generate"
                visible={generateDialog}
                style={{minWidth: '15vw'}}
                onHide={() => setGenerateDialog(false)}
        >
            <GenerateNewReport ids={props.ids}
                               showToast={showToast}
                               close={() => setGenerateDialog(false)}
                               loadingConfig={props.loadingConfig}
            />
        </Dialog>

        <Dialog header={dialogFunction === "REMOVE" ? "Remove reports range" : "Cleanup reports history"}
                visible={removeRangeDialog}
                style={{minWidth: '15vw'}}
                onHide={() => setRemoveRangeDialog(false)}
        >
            <RemoveReportRange ids={props.ids}
                               showToast={showToast}
                               close={() => setRemoveRangeDialog(false)}
                               loadingConfig={props.loadingConfig}
                               function={dialogFunction}
            />
        </Dialog>
    </>
};

export default GeneratedReportTable;